/**
 * The CRF Builder's Visits view when the visits were read from the protocol's
 * sentences, not a schedule table (Jim, 2026-11-04; functions
 * schedule/proseVisits.ts). They are shown with a notice and stay unconfirmed
 * until a person presses Confirm visits or saves an edit to them (check B8 warns
 * until then).
 *
 * studyDesign/config carries `visitsSource: 'protocol_text'` and an empty
 * `visitsConfirmedAt/By` while they wait. Pure, no React or Firebase, so it is
 * tested from functions' vitest web config; the page supplies the user id and
 * the server timestamp.
 */

export const PROSE_VISITS_NOTICE = 'These visits were read from the protocol text, not a schedule table. Please check them.';
export const CONFIRM_VISITS_LABEL = 'Confirm visits';

/** The stored `visitsSource` for visits read from the protocol's sentences. */
const PROTOCOL_TEXT = 'protocol_text';

/** Whether the notice shows: the visits came from the protocol text, there are some, and no one has confirmed them. */
export function proseVisitsNeedConfirmation(config: unknown): boolean {
  const c = config as { visits?: unknown; visitsSource?: unknown; visitsConfirmedAt?: unknown } | null | undefined;
  return (
    !!c &&
    c.visitsSource === PROTOCOL_TEXT &&
    Array.isArray(c.visits) &&
    c.visits.length > 0 &&
    !c.visitsConfirmedAt
  );
}

/** The fields that record who confirmed the visits and when. `stamp` is the server timestamp the page supplies. */
export function confirmationFields(uid: string, stamp: unknown): { visitsConfirmedAt: unknown; visitsConfirmedBy: string } {
  return { visitsConfirmedAt: stamp, visitsConfirmedBy: uid };
}

/** What a save of the visits adds: a person editing and saving visits that waited for confirmation has confirmed them. */
export function fieldsForVisitSave(
  config: unknown,
  uid: string,
  stamp: unknown,
): { visitsConfirmedAt: unknown; visitsConfirmedBy: string } | Record<string, never> {
  return proseVisitsNeedConfirmation(config) ? confirmationFields(uid, stamp) : {};
}
